"use client";

import { useEffect, useRef, useState } from "react";
import { ImageGallery } from "../../components/ImageGallery";
import { BlueskyPost as BlueskyPostComponent } from "../../components/BlueskyPost";
import type { Page, WorkPageContent, DbBlueskyPost } from "../../types/cms";
import type { BlueskyPost as BlueskyPostType } from "../../types/bluesky";

interface WorkCaseStudyClientProps {
  page: Page;
  workInProgressPosts: DbBlueskyPost[];
}

function convertDbPostToApiFormat(post: DbBlueskyPost): BlueskyPostType {
  return {
    uri: post.uri,
    cid: post.cid,
    author: post.author_data,
    record: post.content,
    embed: post.embed_data,
    indexedAt: post.indexed_at,
    replyCount: post.stats.replyCount ?? 0,
    repostCount: post.stats.repostCount ?? 0,
    likeCount: post.stats.likeCount ?? 0
  };
}

export function WorkCaseStudyClient({ page, workInProgressPosts }: WorkCaseStudyClientProps) {
  const content = page.content as WorkPageContent;
  const textColumnRef = useRef<HTMLDivElement>(null);
  const [shouldStick, setShouldStick] = useState(false);

  useEffect(() => {
    const TOP_OFFSET = 96; // aligns with header spacing on large screens
    const BOTTOM_OFFSET = 100; // leaves room above the footer padding

    const measure = () => {
      const node = textColumnRef.current;
      if (!node) return;

      const isDesktop = window.innerWidth >= 1024;
      if (!isDesktop) {
        setShouldStick(false);
        return;
      }

      const availableHeight = window.innerHeight - (TOP_OFFSET + BOTTOM_OFFSET);
      const contentHeight = node.getBoundingClientRect().height;

      setShouldStick(contentHeight <= availableHeight);
    };

    measure();

    const handleResize = () => {
      window.requestAnimationFrame(measure);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("load", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("load", handleResize);
    };
  }, []);

  return (
    <>
      <div className="min-h-screen text-foreground">
        <main className="mt-[50px] pb-[100px] sm:ml-[200px]">
        <div className="w-full max-w-[1600px] px-[20px] sm:px-[50px]">
          <article className="text-[14px] font-mono leading-[1.6]">
            
            <section>
              <div className="mb-[100px]">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  {/* Text content - left side */}
                  <div
                    ref={textColumnRef}
                    className={`space-y-6 ${shouldStick ? "lg:sticky lg:top-24" : ""}`}
                  >
                    <h1 className="text-[14px] font-medium mb-2">
                      {content.title}
                    </h1>

                    {/* Overview text */}
                    <div
                      className="rich-text-content"
                      style={{ lineHeight: "1.6" }}
                      dangerouslySetInnerHTML={{
                        __html: content.overview_text || content.content || ''
                      }}
                    />

                    {/* Detail text - only show if it exists and is different from overview */}
                    {content.detail_text && content.detail_text.trim() !== '' && (
                      <div
                        className="rich-text-content"
                        style={{ lineHeight: "1.6" }}
                        dangerouslySetInnerHTML={{
                          __html: content.detail_text
                        }}
                      />
                    )}
                  </div>
                  
                  {/* Images - right side */}
                  {content.images && content.images.length > 0 && (
                    <div className="lg:col-span-2">
                      <ImageGallery 
                        images={content.images.map(img => ({ src: img.src, alt: img.alt }))} 
                      />
                    </div>
                  )}
                </div>
              </div>
            </section>

            {workInProgressPosts.length > 0 && (
              <section className="pt-[60px]">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  {/* Text and description - left side */}
                  <div className="space-y-2">
                    <h2 className="text-[14px] font-medium">
                      This case in progress
                    </h2>
                    <p className="text-[14px] leading-[1.6]">
                      The following are snapshots of the project. Ideas that shaped it. Experiments that may never be live, but explore the widths and depths of the concept.
                    </p>
                  </div>

                  {/* Posts - right side */}
                  <div className="lg:col-span-2 space-y-[80px]">
                    {workInProgressPosts.map(post => (
                      <div key={post.id}>
                        <BlueskyPostComponent post={convertDbPostToApiFormat(post)} />
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            )}
          </article>
          </div>
        </main>
      </div>
    </>
  );
}
